<template>
  <div class="Top">
    <div class="CarouselWrapper">
      <Carousel mode="pm" :contents="carouselContents" :interval="5000" />
    </div>

    <div class="PickupSections">
      <template v-for="data in pokemonSinglePickupsForLatestPacks" :key="data.packId">
        <div class="PickupSection" v-if="!!data.hits.length">
          <div class="SectionTitle">
            <Thunder />
            {{ data.sectionTitle }}
          </div>
          <SummaryList
            mode="horizontal"
            show-read-more
            :read-more-link="`/pokemon/search?packId=${data.packId}`"
            :read-more-label="data.readMoreLabel"
            :items="data.hits"
            item-class-name="from-pickup-section-for-latestPacks"
            />
        </div>
      </template>

      <div class="PickupSection">
        <div class="SectionTitle">
          <Thunder />
          人気エクストラコレクション
        </div>
        <SummaryList
          mode="horizontal"
          show-read-more
          read-more-link="/pokemon/pickups/extra"
          read-more-label="「エクストラコレクション」一覧"
          :items="pokemonSinglePickupExtraProducts"
          item-class-name="from-pickup-section-for-extra"
          />
      </div>

      <div class="PickupSection" v-if="!!pokemonSinglePickupPriceDownProducts.length">
        <div class="SectionTitle">
          <Thunder />
          値下げ商品
        </div>
        <SummaryList
          mode="horizontal"
          show-read-more
          read-more-link="/pokemon/pickups/price-down"
          read-more-label="値下げ商品一覧"
          :items="pokemonSinglePickupPriceDownProducts"
          item-class-name="from-pickup-section-for-priceDown"
          />
      </div>

      <div class="PickupSection">
        <div class="SectionTitle">
          <Thunder />
          汎用カード
        </div>
        <SummaryList
          mode="horizontal"
          show-read-more
          read-more-link="/pokemon/pickups/general-purpose"
          read-more-label="汎用カード一覧"
          :items="pokemonSinglePickupGeneralPurposeProducts"
          item-class-name="from-pickup-section-for-generalPurpose"
          />
      </div>

      <div class="PickupSection">
        <div class="SectionTitle">
          <Thunder />
          スペシャルアートレア
        </div>
        <SummaryList
          mode="horizontal"
          show-read-more
          read-more-link="/pokemon/pickups/sar"
          read-more-label="スペシャルアートレア一覧"
          :items="pokemonSinglePickupSARProducts"
          item-class-name="from-pickup-section-for-sar"
          />
      </div>
    </div>
    <section class="PopularProducts">
      <div class="SectionTitle">
        <Thunder />
        いま売れている人気カード
      </div>
      <div class="PopularProductsList" ref="popularProductsListRef">
        <div class="SummaryWrapper"
          v-for="product in lastSoldSingleProductSearchResults"
          :key="product.inventory_product_id"
          :style="{'min-height': `${summaryWrapperMinHeight}px`}"
        >
          <Summary class="Summary from-pickup-section-for-popular" :data="product" :disable-view-transition />
        </div>
      </div>
      <div class="PopularProductsReadMoreButtonWrapper">
        <Button
          :disabled="isSearchedAllLastSoldSingleProducts"
          width="100%"
          text="さらに表示"
          mode="secondary"
          @click="readMore"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, ref } from 'vue'
import { defineNuxtComponent, useNuxtApp, useAsyncData } from '#imports'
import { storeToRefs } from 'pinia'
import type { ESPokemonSingleProduct } from "@ka-nabellinc/tcg-platform-es-schema";
import { Button, Carousel } from '@ka-nabellinc/ka-nabell-ui'
// @ts-expect-error
import { Thunder } from '@ka-nabellinc/ka-nabell-icons'
import SummaryList from '@/components/Pokemon/Single/SummarList'
import Summary from '@/components/Pokemon/Single/Summary'
import { DITokens } from '@/di'
import type { SellingBanner } from '@/types/sellingBanner'
import { SellingBanners } from './banners'
import dayjs from 'dayjs'

const publishFilterFn = (now: dayjs.Dayjs) => (item: SellingBanner) => {
  if (now.isBefore(item.startDate)) {
    return false
  }
  if (!!item.endDate && now.isAfter(item.endDate)) {
    return false
  }

  return true
}

export default defineNuxtComponent({
  components: {
    Button,
    Carousel,
    Thunder,
    SummaryList,
    Summary,
  },
  props: {
    pokemonSinglePickupsForLatestPacks: {
      type: Array as () => Array<{ packId: number; sectionTitle: string; readMoreLabel: string; hits: ESPokemonSingleProduct[] }>,
      default: []
    },
    pokemonSinglePickupGeneralPurposeProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    },
    pokemonSinglePickupSARProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    },
    pokemonSinglePickupExtraProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    }
    ,
    pokemonSinglePickupPriceDownProducts: {
      type: Array as () => ESPokemonSingleProduct[],
      default: []
    }
  },
  async setup() {
    const { $container } = useNuxtApp()
    const cartStore = $container.resolve(DITokens.CartStore);
    const cartState = storeToRefs(cartStore);
    const disableViewTransition = computed(() => cartState.showCart.value) // カート表示中はview transitionをoff
    const pokemonStore = $container.resolve(DITokens.PokemonStore)
    const {
      searchingLastSoldSingleProduct,
      lastSoldSingleProductSearchResults,
      lastSoldSingleProductSearchTotal,
      lastSoldSingleProductSearchCursor,
    } = storeToRefs(pokemonStore)

    const isSearchedAllLastSoldSingleProducts = 
      computed(() => lastSoldSingleProductSearchTotal.value <= lastSoldSingleProductSearchResults.value.length)

    await useAsyncData('LAST_SOLD_POKEMON_SEARCH', async () => {
      // hydration後のduplicated callを抑制するためにuseAsyncDataから値を返却
      if (lastSoldSingleProductSearchResults.value.length > 0) return
      return await pokemonStore.searchLastSoldSingleProduct(6)
    })

    const readMore = () => {
      // カーソルがない場合は追加読み込み不可
      if (!lastSoldSingleProductSearchCursor.value) return
      pokemonStore.searchLastSoldSingleProduct(15, lastSoldSingleProductSearchCursor.value)
    }

    // Layout Shift 対策として SummaryWrapper に min-height を設定する
    const popularProductsListRef = ref<HTMLDivElement>()
    const summaryWrapperWidth = computed(() => 
      !!popularProductsListRef.value ?
        (popularProductsListRef.value.clientWidth - 16 * 2) / 3 : undefined
    )
    const summaryWrapperMinHeight = computed(() => 
      // width * 1.3968(アスペクト比) + 59.5px(商品名などのラベルの高さ)
      typeof summaryWrapperWidth.value === 'number' ?
        summaryWrapperWidth.value * 1.3968 + 59.5 : undefined
    )

    const now = dayjs()
    const carouselContents = SellingBanners.filter(publishFilterFn(now)).map((item) => ({
      src: item.bannerImageUrl,
      link: item.linkUrl,
      alt: item.bannerTitle,
      isLinkExternal: item.targetAttribute === '_blank'
    }))

    return {
      carouselContents,
      searchingLastSoldSingleProduct,
      lastSoldSingleProductSearchResults,
      isSearchedAllLastSoldSingleProducts,
      popularProductsListRef,
      summaryWrapperMinHeight,
      readMore,
      disableViewTransition,
    }
  }
})
</script>

<style scoped>
.Top {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px
}

.PickupSections {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.PickupSection {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.PopularProducts {
  width: 100%;
  display: flex;
  gap: 20px;
  flex-direction: column;
}

.SectionTitle {
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: 0.32px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;
}

.PopularProductsList {
  position: relative;
  width: 100%;
  display: flex;
  column-gap: 12px;
  row-gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  padding: 0 16px;
}

.PopularProductsReadMoreButtonWrapper {
  width: 100%;
  padding: 0 16px;
}

.SummaryWrapper {
  width: calc((100% - (12px * 2)) / 3);
}

.LoadingWrapper {
  margin: 0 auto;
}

.CarouselWrapper {
  width: 100%;
  background: linear-gradient(180deg, transparent 0%, transparent 40%, var(--primitive-colors-basic-yellow-100) 40%, var(--primitive-colors-basic-yellow-100) 100%);
  border-bottom: 1px solid var(--primitive-colors-basic-yellow-300);
  padding: 12px 0;
}
</style>
